import { MouseEvent, ReactNode, useCallback, useMemo } from 'react'
import { StoryPeerType } from '@weblite/ts-action/story'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
// Material
import { Skeleton } from '@mui/material'
// Logic
import { useHall } from '$logic/hall/react-hook'
// Helpers
import { getImageUrl } from '$func/imageUrl'
import { stringToColor } from '$func/string-to-color'
import { getAvatarText } from '$func/avatar-text'
import { useHallAvatarStories } from '$logic/story/react-hooks'
import { StoryAvatarBadge } from '$comp/_app/components/story/story-avatar-badge'
import { useRoom } from '$logic/room'
import { showCreateStoryModal } from '$comp/modals/create-story/create-story.api'
import { OpenStorySource, StoryAnalyticsApi } from '$logic/story/analytics'
import { openStory } from '$logic/story/service'
import { AVATAR_SIZES } from '$common/avatar/avatar-base/conf'
import { SelectableAvatar, SelectableAvatarProps } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { useJoinCallWithTimeout } from '$logic/call'
import { showImageViewerModal } from '$comp/modals/image-viewer'
import { showRoomProfileMenu } from '$comp/modals/menus/room-profile-menu'

interface Props
  extends Omit<
    SelectableAvatarProps,
    | 'avatarBackgroundText'
    | 'alt'
    | 'storyCount'
    | 'seenStoryCount'
    | 'src'
    | 'storyLoading'
  > {
  roomId: string
  overrideImage?: string
  selected?: boolean
  children?: ReactNode
  withStory?: boolean
  canClientUserCreateStory?: boolean
  disableClick?: boolean
  source?: OpenStorySource
}

/**
 * - TODO: background and color
 * - NOTE: Mui uses the first letter of alt in broken links
 */
export function HallAvatar({
  overrideImage,
  roomId,
  disableClick = false,
  onClick,
  children = null,
  checked = false,
  withStory,
  canClientUserCreateStory,
  ongoingCallType,
  source,
  size = AvatarSize.SM,
  ...rest
}: Props) {
  const isInStoryRibbon = source === OpenStorySource.STORY_RIBBON
  const { entity: hall, err, loading } = useHall(roomId)
  const { joiningCall, joinCall } = useJoinCallWithTimeout()
  const room = useRoom(roomId)
  const {
    storyCount,
    seenStoryCount,
    showCreateStoryBadge,
    uploading: uploadingStory,
  } = useHallAvatarStories(
    roomId,
    room?.type as StoryPeerType,
    withStory,
    canClientUserCreateStory,
  )

  const backgroundColor = stringToColor(hall?.name, hall?.roomId)
  const { image } = hall || {}
  const avatarSrc = getImageUrl(
    overrideImage !== undefined ? overrideImage : image,
  )

  const openImageViewerHandler = useCallback(() => {
    if (avatarSrc) showImageViewerModal({ media: { src: avatarSrc } })
  }, [avatarSrc])

  const handleStoryClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (checked) return

      if (canClientUserCreateStory && showCreateStoryBadge) {
        if (room) {
          e?.stopPropagation()
          showCreateStoryModal({
            peerId: roomId,
            peerType: room.type as StoryPeerType,
          })
        }
        return
      }
      if (room && storyCount) {
        e?.stopPropagation()
        StoryAnalyticsApi.open(
          roomId,
          room.type as StoryPeerType,
          source || OpenStorySource.UNKNOWN,
        )
        const elem = (e?.target as HTMLDivElement)?.getBoundingClientRect()

        openStory({
          peerId: roomId,
          peerType: room.type as StoryPeerType,
          xAxis: elem?.x,
          yAxis: elem?.y,
          singleMode: !isInStoryRibbon,
        })
      }
    },
    [
      roomId,
      checked,
      canClientUserCreateStory,
      storyCount,
      showCreateStoryBadge,
    ],
  )

  const handleJoinCall = useCallback(() => {
    if (!room || !room.call || joiningCall) return
    joinCall(roomId, room.type, room.call.type)
  }, [joinCall, joiningCall])

  const hasUploadedStories = canClientUserCreateStory
    ? !showCreateStoryBadge
    : storyCount > 0

  const onStoryItemClick = useCallback(
    async (e: MouseEvent<HTMLDivElement>) => {
      await closeCurrentModal()
      handleStoryClick(e)
    },
    [handleStoryClick],
  )

  const onProfileItemClick = useCallback(async () => {
    await closeCurrentModal()
    openImageViewerHandler()
  }, [openImageViewerHandler])

  const handleAvatarInRoomProfileClick = useCallback(
    (e: any) => {
      // NOTE: Click on avatar scenarios are written in Notion documentations
      if (hasUploadedStories && avatarSrc) {
        showRoomProfileMenu({
          hasStory: hasUploadedStories,
          xAxis: e.clientX,
          yAxis: e.clientY,
          onStoryClick: onStoryItemClick,
          onProfileClick: onProfileItemClick,
        })
      } else if (!hasUploadedStories && avatarSrc) {
        if (canClientUserCreateStory) {
          showRoomProfileMenu({
            hasStory: hasUploadedStories,
            xAxis: e.clientX,
            yAxis: e.clientY,
            onStoryClick: onStoryItemClick,
            onProfileClick: onProfileItemClick,
          })
        } else openImageViewerHandler()
      } else if (hasUploadedStories && !avatarSrc) {
        handleStoryClick(e)
      } else {
        if (canClientUserCreateStory) {
          handleStoryClick(e)
        }
      }
    },
    [
      hasUploadedStories,
      avatarSrc,
      canClientUserCreateStory,
      handleStoryClick,
      openImageViewerHandler,
      onStoryItemClick,
      onProfileItemClick,
    ],
  )

  const handleClick = useMemo(() => {
    if (disableClick) return undefined
    if (ongoingCallType) return isInStoryRibbon ? handleJoinCall : undefined

    if (source === OpenStorySource.ROOM_PROFILE)
      return handleAvatarInRoomProfileClick

    if (withStory) return handleStoryClick

    return undefined
  }, [
    withStory,
    onClick,
    handleStoryClick,
    disableClick,
    handleJoinCall,
    isInStoryRibbon,
    handleAvatarInRoomProfileClick,
  ])

  if (loading || err)
    return (
      <Skeleton
        variant="circular"
        width={AVATAR_SIZES[size]}
        height={AVATAR_SIZES[size]}
      />
    )

  return (
    <StoryAvatarBadge
      onClick={handleClick}
      invisible={!showCreateStoryBadge || !!ongoingCallType}
    >
      <SelectableAvatar
        storyLoading={uploadingStory}
        joiningCall={joiningCall}
        storyCount={ongoingCallType ? undefined : storyCount}
        seenStoryCount={ongoingCallType ? undefined : seenStoryCount}
        ongoingCallType={ongoingCallType}
        avatarBackgroundText={children || getAvatarText(hall?.name)}
        src={avatarSrc}
        backgroundColor={backgroundColor}
        alt={hall?.name.toUpperCase()}
        checked={checked}
        size={size}
        {...rest}
      />
    </StoryAvatarBadge>
  )
}
